import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Zap, Shield, Activity, Plus, Edit, Trash2, Copy, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_BASE}/api`;

const TYPE_META = {
  continuity: { label: i18n.t('auto.lbl.earthContinuity'), icon: Activity, prefix: 'EC' },
  insulation: { label: i18n.t('auto.lbl.insulationResistance'), icon: Shield, prefix: 'IR' },
  fault_loop: { label: i18n.t('auto.lbl.earthFaultLoop'), icon: Zap, prefix: 'FL' },
};

const TestReportTemplateSettings = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('continuity');
  const [editing, setEditing] = useState(null); // template being edited in dialog

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/test-reports/templates`, authHeader);
      setTemplates(resp.data);
    } catch { toast.error(t('auto.set.failedToLoadTemplates')); }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const templatesByType = useMemo(() => {
    const groups = { continuity: [], insulation: [], fault_loop: [] };
    templates.forEach(t => { if (groups[t.test_type]) groups[t.test_type].push(t); });
    return groups;
  }, [templates]);

  const duplicate = async (tpl) => {
    try {
      await axios.post(`${API}/test-reports/templates/${tpl.id}/duplicate`, {}, authHeader);
      toast.success(t('auto.set.templateDuplicated'));
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (tpl) => {
    if (!window.confirm(`Delete "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/test-reports/templates/${tpl.id}`, authHeader);
      toast.success(t('auto.set.deleted'));
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const toggleActive = async (tpl) => {
    try {
      await axios.put(`${API}/test-reports/templates/${tpl.id}`, { is_active: !tpl.is_active }, authHeader);
      fetchTemplates();
    } catch (e) { toast.error(t('auto.set.failed')); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const payload = {
        name: editing.name,
        doc_ref: editing.doc_ref,
        revision: editing.revision,
        standard_reference: editing.standard_reference,
        description: editing.description,
        default_rows: parseInt(editing.default_rows, 10) || 5,
      };
      if (editing.id) {
        await axios.put(`${API}/test-reports/templates/${editing.id}`, payload, authHeader);
        toast.success(t('auto.set.templateUpdated'));
      } else {
        await axios.post(`${API}/test-reports/templates`, { ...payload, test_type: editing.test_type }, authHeader);
        toast.success(t('auto.set.templateCreated'));
      }
      setEditing(null);
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const startNew = () => setEditing({
    name: `${TYPE_META[activeType].label} Test Report`,
    test_type: activeType,
    doc_ref: `EFX-EL-TR-${TYPE_META[activeType].prefix}-002`,
    revision: 'Rev 01',
    standard_reference: '',
    description: '',
    default_rows: 5,
  });

  if (loading) {
    return <div className="py-8 text-center text-sm text-gray-400">{t('auto.set.loading2')}</div>;
  }

  return (
    <div className="space-y-4" data-testid="test-report-template-settings">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="h-5 w-5 text-orange-500" />
            {t('auto.set.electricalTestReportTemplates')}
          </CardTitle>
          <CardDescription>
            EN 60204-1 — configure templates for Earth Continuity, Insulation Resistance and Fault Loop reports.
            Report numbering is automatic: <code>{t('auto.set.ecYyyyNnn')}</code> / <code>{t('auto.set.irYyyyNnn')}</code> / <code>{t('auto.set.flYyyyNnn')}</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeType} onValueChange={setActiveType}>
            <TabsList className="flex flex-wrap h-auto gap-1 p-1 mb-3 w-full justify-start">
              {Object.entries(TYPE_META).map(([k, m]) => {
                const Icon = m.icon;
                return (
                  <TabsTrigger key={k} value={k} data-testid={`tpl-tab-${k}`}>
                    <Icon className="h-4 w-4 mr-1.5" /> {m.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {Object.keys(TYPE_META).map(type => (
              <TabsContent key={type} value={type} className="space-y-2">
                <div className="flex justify-end">
                  <Button size="sm" onClick={startNew} data-testid={`new-tpl-${type}-btn`}>
                    <Plus className="h-4 w-4 mr-1" /> {t('auto.set.newTemplate')}
                  </Button>
                </div>
                {templatesByType[type]?.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-400 border-2 border-dashed rounded-lg">
                    No templates for {TYPE_META[type].label} yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {templatesByType[type]?.map(tpl => (
                      <div key={tpl.id} className="border rounded-lg p-3 flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium text-sm">{tpl.name}</div>
                            {tpl.is_active === false && <Badge variant="outline" className="text-xs">{t('auto.set.inactive')}</Badge>}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 font-mono">
                            {tpl.doc_ref} · {tpl.revision} · {tpl.standard_reference}
                          </div>
                          {tpl.description && (
                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">{tpl.description}</div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {tpl.columns?.length || 0} columns · {tpl.default_rows} default rows · {tpl.sign_off_roles?.length || 0} sign-offs
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => setEditing(tpl)} data-testid={`edit-tpl-${tpl.id}`}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => duplicate(tpl)} title={t('auto.set.duplicate')}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => toggleActive(tpl)} title={tpl.is_active === false ? t('auto.set.activate') : t('auto.set.deactivate')}>
                            <span className={`text-xs ${tpl.is_active === false ? 'text-gray-400' : 'text-green-600'}`}>
                              {tpl.is_active === false ? t('auto.set.off') : t('auto.set.on')}
                            </span>
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => remove(tpl)} title={t('auto.set.delete')}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? t('auto.set.editTemplate') : t('auto.set.newTemplate')}</DialogTitle>
            <DialogDescription>{t('auto.set.configureTheReportTemplateNameDocument')}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-600">{t('auto.set.name')}</label>
                <Input value={editing.name || ''} onChange={(e) => setEditing(prev => ({ ...prev, name: e.target.value  }))} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-600">{t('auto.set.documentReference')}</label>
                  <Input value={editing.doc_ref || ''} onChange={(e) => setEditing(prev => ({ ...prev, doc_ref: e.target.value  }))} />
                </div>
                <div>
                  <label className="text-xs text-gray-600">{t('auto.set.revision')}</label>
                  <Input value={editing.revision || ''} onChange={(e) => setEditing(prev => ({ ...prev, revision: e.target.value  }))} />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600">{t('auto.set.standardReference')}</label>
                <Input value={editing.standard_reference || ''} onChange={(e) => setEditing(prev => ({ ...prev, standard_reference: e.target.value  }))} />
              </div>
              <div>
                <label className="text-xs text-gray-600">{t('auto.set.description')}</label>
                <Textarea rows={4} value={editing.description || ''} onChange={(e) => setEditing(prev => ({ ...prev, description: e.target.value  }))} />
              </div>
              <div>
                <label className="text-xs text-gray-600">{t('auto.set.defaultRows')}</label>
                <Input type="number" min="1" max="100" value={editing.default_rows || 5} onChange={(e) => setEditing(prev => ({ ...prev, default_rows: e.target.value  }))} />
              </div>
              <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded border-l-2 border-gray-200">
                Columns and header fields are fixed per template type and use the EN 60204-1 defaults.
                To customise them, duplicate the template and edit the JSON via API.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="dialog-cancel-btn">{t('auto.set.cancel')}</Button>
            <Button onClick={saveEdit} data-testid="save-tpl-btn"><Save className="h-4 w-4 mr-1" /> {t('auto.set.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestReportTemplateSettings;
